import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { IRiskFilters, IRiskTemplateResponse } from "@gloria-public/risk/domain/interfaces/risk.interface";
import { RiskService } from "@gloria-public/risk/domain/services/risk.service";
import { TranslateService } from "@ngx-translate/core";
import { BackendTableDataSource } from "@shared/utils/backend-table.datasource";
import { Subject, combineLatest, distinctUntilChanged, of, startWith, switchMap, takeUntil } from "rxjs";

@Component({
  selector: "app-risks-page",
  templateUrl: "./risks-page.component.html",
  styleUrls: ["./risks-page.component.scss"],
})
export class RisksPageComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  public dataSource: BackendTableDataSource<IRiskTemplateResponse>;
  public totalCount = 0;
  public filters: IRiskFilters = { page: 1, pageSize: 10, tags: [] };

  public readonly pageSizeOptions = [5, 10, 20, 50];

  /** Emits when the component is destroyed, used to complete all active subscriptions */
  private onDestroy$ = new Subject<void>();

  constructor(
    private riskService: RiskService,
    private translateService: TranslateService,
    public snackBar: MatSnackBar,
    private router: Router
  ) {
    this.dataSource = new BackendTableDataSource(
      (p) =>
        combineLatest([
          of(p),
          this.translateService.onLangChange.pipe(
            startWith({ lang: this.translateService.currentLang }),
            distinctUntilChanged((a, b) => a.lang === b.lang),
            takeUntil(this.onDestroy$)
          ),
        ]).pipe(
          switchMap(([params, event]) => this.riskService.getRisks({ ...params, lang: event.lang })),
          takeUntil(this.onDestroy$)
        ),
      (p) => this.riskService.getRisksCount({ tags: p["tags"] as string[] | undefined })
    );
  }

  /**
   * Loads the Datasource and subscribe to its count.
   */
  ngOnInit(): void {
    this.dataSource.count$.pipe(takeUntil(this.onDestroy$)).subscribe((count) => (this.totalCount = count));
    this.loadDataSource();
  }

  ngAfterViewInit(): void {
    this.paginator?.page.pipe(takeUntil(this.onDestroy$)).subscribe((event: PageEvent) => {
      this.filters = { ...this.filters, page: event.pageIndex + 1, pageSize: event.pageSize };
      this.loadDataSource();
    });
  }

  /**
   * Cleans up subscriptions and subjects when the component is destroyed.
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Loads risks using current filters
   */
  private loadDataSource(): void {
    this.dataSource.loadData({
      page: this.filters.page ?? 1,
      pageSize: this.filters.pageSize ?? 10,
      ...(this.filters.tags?.length ? { tags: this.filters.tags } : {}),
      ...(this.filters.sortByDate ? { sortByDate: this.filters.sortByDate } : {}),
    });
  }

  /**
   * Reloads the risks list with new tag filters
   * @param tags List of selected risk tags
   */
  public onTagsChanged(tags: string[]): void {
    this.filters = {
      ...this.filters,
      page: 1,
      tags: tags,
    };

    this.loadDataSource();
  }

  /**
   * Navigates to a specific risk detail page
   * @param id Risk identifier
   */
  public goToRisk(id: string): void {
    this.router.navigate(["/risks", id]);
  }
}
